import React from 'react';
import EditIcon from '@material-ui/icons/Edit';
import {
    Button,
    DeleteButton,
    List,
    NumberField,
    TopToolbar,
    useDataProvider,
    useNotify,
    useRedirect
} from 'react-admin';
import AdminPagination from '../AdminPagination';
import AdminSelectableDatagrid from '../AdminSelectableDatagrid';
import { ResourceListTitle } from '../AdminResourceTotalCount';

const cleanSuggestedTitle = (title: string = '', artist: string = '') => {
    const cleanTitle = title.trim();
    const cleanArtist = artist.trim();

    if (!cleanArtist) {
        return cleanTitle;
    }

    const escapedArtist = cleanArtist.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const artistPrefix = new RegExp(
        `^\\s*${escapedArtist}\\s*[-–—:|]\\s*`,
        'i',
    );

    return cleanTitle.replace(artistPrefix, '').trim();
};

const getConsistentIds = (songs: any[], field: string): string[] => {
    const classifications = songs
        .map((song) =>
            Array.isArray(song[field])
                ? song[field].map(String).sort()
                : [],
        )
        .filter((ids) => ids.length > 0);

    if (!classifications.length) {
        return [];
    }

    const reference = JSON.stringify(classifications[0]);

    return classifications.every(
        (ids) => JSON.stringify(ids) === reference,
    )
        ? classifications[0]
        : [];
};

const MasteryField = ({ record }: any) => {
    if (!record) {
        return null;
    }

    const labels: string[] = [];

    if ((record.mastery_melody_count || 0) > 0) {
        labels.push('Mélodie');
    }

    if ((record.mastery_chorus_count || 0) > 0) {
        labels.push('Refrain');
    }

    if ((record.mastery_full_song_count || 0) > 0) {
        labels.push('Chanson');
    }

    return (
        <span
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {labels.join(', ') || '—'}
        </span>
    );
};

const SuggestionStatusField = ({ record }: any) => {
    if (!record) {
        return null;
    }

    if (record.song_id) {
        return (
            <span style={{ whiteSpace: 'nowrap' }}>
                <span
                    style={{
                        display: 'inline-block',
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        backgroundColor: '#2e7d32',
                        marginRight: 7,
                    }}
                />
                Traité
            </span>
        );
    }

    if ((record.request_count || 0) >= 4) {
        return (
            <span style={{ whiteSpace: 'nowrap' }}>
                <span
                    style={{
                        display: 'inline-block',
                        width: 9,
                        height: 9,
                        borderRadius: '50%',
                        backgroundColor: '#f57c00',
                        marginRight: 7,
                    }}
                />
                Priorité
            </span>
        );
    }

    return <span style={{ whiteSpace: 'nowrap' }}>À traiter</span>;
};

const CenteredTextField = ({ record, source }: any) => {
    if (!record) {
        return null;
    }

    return (
        <span
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {record[source] || '—'}
        </span>
    );
};

const ShortTitleField = ({ record }: any) => {
    if (!record) {
        return null;
    }

    const title = cleanSuggestedTitle(
        String(record.title || ''),
        String(record.artist || ''),
    );

    const displayedTitle =
        title.length > 30
            ? `${title.slice(0, 30)}…`
            : title;

    return (
        <span
            title={title}
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {displayedTitle}
        </span>
    );
};

const SingleLineDateField = ({ record, source }: any) => {
    if (!record || !record[source]) {
        return <span>—</span>;
    }

    const date = new Date(record[source]);

    const value = date.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
    });

    return (
        <span
            style={{
                display: 'block',
                width: '100%',
                textAlign: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            {value}
        </span>
    );
};

const CreateSongButton = ({ record }: any) => {
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const redirect = useRedirect();

    if (!record) {
        return null;
    }

    const handleClick = async () => {
        try {
            await dataProvider.startSongSuggestion(record.id);

            const existingSongsResponse = record.artist
                ? await dataProvider.getList('song', {
                    pagination: { page: 1, perPage: 100 },
                    sort: { field: 'artist', order: 'ASC' },
                    filter: { artist: record.artist },
                })
                : { data: [] };

            const existingSongs = existingSongsResponse.data || [];
            const songgenreIds = getConsistentIds(
                existingSongs,
                'songgenre_ids',
            );
            const songregionIds = getConsistentIds(
                existingSongs,
                'songregion_ids',
            );

            const references = Array.isArray(record.youtube_references)
                ? [...record.youtube_references]
                : [];

            references.sort(
                (a, b) =>
                    (b.request_count || 0) - (a.request_count || 0),
            );

            const preview =
                references.length > 0
                    ? references[0].video_id || ''
                    : '';

            redirect(
                'create',
                '/song',
                undefined,
                undefined,
                {
                    record: {
                        artist: record.artist || '',
                        title: cleanSuggestedTitle(
                            record.title || '',
                            record.artist || '',
                        ),
                        preview,
                        video: 'vdocipher',
                        published: false,
                        is_demo: false,
                        is_special: false,
                        promote: 'new',
                        songgenre_ids: songgenreIds,
                        songregion_ids: songregionIds,
                        songlang_ids: [],
                        groupType: 'solo',
                        voiceType: 'male',
                    },
                    songSuggestionId: record.id,
                },
            );
        } catch (error) {
            const message =
                error instanceof Error
                    ? error.message
                    : 'Impossible de préparer la suggestion.';

            notify(message, 'warning');
        }
    };

    return (
        <Button
            label=""
            onClick={handleClick}
            title="Song"
        >
            <EditIcon />
        </Button>
    );
};

const SuggestionActionsField = ({ record }: any) => {
    if (!record) {
        return null;
    }

    return (
        <span
            style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                whiteSpace: 'nowrap',
            }}
        >
            <CreateSongButton record={record} />
            <DeleteButton
                record={record}
                resource="song-suggestion"
                undoable={false}
                label=""
                title="Supprimer"
                confirmTitle={`Supprimer : ${record.artist || ''} — ${cleanSuggestedTitle(
                    record.title || '',
                    record.artist || '',
                )} ?`}
                confirmContent="Cette suggestion sera définitivement supprimée."
            />
        </span>
    );
};

const SongCreatedEditField = ({ record }: any) => {
    const redirect = useRedirect();
    if (record == null || record.song_id == null) return null;
    return (
        <Button
            label=""
            onClick={() => redirect("edit", "/song", record.song_id)}
            title="Modifier la Song"
        >
            <EditIcon />
        </Button>
    );
};

const SuggestionNavigation = ({ archive = false }: any) => (
    <>
        <TopToolbar />
        <div
            style={{
                paddingLeft: 16,
                paddingRight: 16,
                paddingTop: 16,
                marginBottom: 20,
            }}
        >
            <a
                href={archive ? '#/song-suggestion' : '#/song-suggestion-archives'}
                style={{ textDecoration: 'none', fontWeight: 600 }}
            >
                {archive ? '← À traiter' : 'Historique'}
            </a>
        </div>
    </>
);

const SuggestionTableContainer = ({ children }: any) => (
    <div
        style={{
            width: '100%',
            maxWidth: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
        }}
    >
        {children}
    </div>
);

export const SongSuggestionList = (props) => (
    <List
        {...props}
        title={
            <ResourceListTitle
                resource="song-suggestion"
                label="Suggestions — À traiter"
                queryString="song_id=null"
            />
        }
        actions={false}
        bulkActionButtons={false}
        sort={{ field: 'request_count', order: 'DESC' }}
        filter={{ song_id: null }}
        perPage={50}
        pagination={<AdminPagination />}
        exporter={false}
    >
        <>
            <SuggestionNavigation />
            <SuggestionTableContainer>
            <AdminSelectableDatagrid>
            <NumberField
                source="request_count"
                label="Demandes"
                textAlign="center"
            />

            <SuggestionStatusField
                label="Statut"
                textAlign="center"
            />

            <CenteredTextField
                source="artist"
                label="Artiste"
                textAlign="center"
            />

            <ShortTitleField
                label="Titre"
                textAlign="center"
            />

            <MasteryField
                label="Connaissance"
                textAlign="center"
            />

            <SingleLineDateField
                source="last_requested_at"
                label="Dernière demande"
                textAlign="center"
            />

            <SuggestionActionsField
                label="Actions"
                textAlign="center"
            />
        </AdminSelectableDatagrid>
            </SuggestionTableContainer>
        </>
    </List>
);

export const SongSuggestionArchiveList = (props) => (
    <List
        {...props}
        title="Suggestions — Archives"
        actions={false}
        bulkActionButtons={false}
        sort={{ field: 'last_requested_at', order: 'DESC' }}
        filter={{ view: 'history' }}
        perPage={50}
        pagination={<AdminPagination />}
        exporter={false}
    >
        <>
            <SuggestionNavigation archive />
            <SuggestionTableContainer>
            <AdminSelectableDatagrid>
            <NumberField
                source="request_count"
                label="Demandes"
                textAlign="center"
            />

            <SuggestionStatusField
                label="Statut"
                textAlign="center"
            />

            <CenteredTextField
                source="artist"
                label="Artiste"
                textAlign="center"
            />

            <ShortTitleField
                label="Titre"
                textAlign="center"
            />

            <SingleLineDateField
                source="first_requested_at"
                label="Première demande"
                textAlign="center"
            />

            <SingleLineDateField
                source="last_requested_at"
                label="Dernière demande"
                textAlign="center"
            />

            <SongCreatedEditField
                label="Chanson créée"
                textAlign="center"
            />
        </AdminSelectableDatagrid>
            </SuggestionTableContainer>
        </>
    </List>
);

export default SongSuggestionList;
